import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import RevisionReplyAdjuntos from "@/components/RevisionReplyAdjuntos";
import { revisionBlockPreview } from "@/components/RevisionBloquesPreview";

const EMOJI_PREDETERMINADO = "📄";

function renderLinkedText(text) {
  const value = String(text || "");
  const regex = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
  const isUrlLike = (part) => /^https?:\/\/\S+$/i.test(part) || /^www\.\S+$/i.test(part);
  const parts = value.split(regex);
  return parts.map((part, idx) => {
    if (isUrlLike(part)) {
      const href = part.startsWith("http") ? part : `https://${part}`;
      return <a key={idx} href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline break-all">{part}</a>;
    }
    return <span key={idx}>{part}</span>;
  });
}

function formatFecha(iso) {
  const d = new Date(iso || "");
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}h | ${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}.${d.getFullYear()}`;
}

const readAnyFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    if (!file) return reject(new Error("Sin archivo"));
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = () => reject(new Error("Error al leer"));
    r.readAsDataURL(file);
  });

/**
 * Vista pública de una hoja de trabajo (revisión): portada, pestañas, formulario de respuesta e historial.
 * @param {object} props
 * @param {object|null} props.data - Payload de getSharedRevision / getSharedRevisionRespuesta
 * @param {boolean} props.initialLoading - Primer fetch en curso
 * @param {() => Promise<void>} props.onReload - Vuelve a cargar datos tras enviar respuesta
 * @param {(payload: { mensaje: string, snapshot_id: string, adjuntos?: object[] }) => Promise<void>} props.submitReply
 */
export default function RevisionPublicSheet({ data, initialLoading, onReload, submitReply }) {
  const [reply, setReply] = useState("");
  const [replyFiles, setReplyFiles] = useState([]);
  const [sendingReply, setSendingReply] = useState(false);
  const [activeSnapshotId, setActiveSnapshotId] = useState("");
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!data) return;
    setActiveSnapshotId((prev) => prev || data.latest_snapshot_id || (data.snapshots || [])[0]?.id || "");
  }, [data]);

  if (initialLoading && !data) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-background">
        <Loader2 className="w-7 h-7 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!data) return null;

  const snapshots = Array.isArray(data.snapshots) ? data.snapshots : [];
  const activeSnapshot = snapshots.find((s) => s.id === activeSnapshotId) || snapshots[0] || null;
  const timeline = Array.isArray(data.timeline) ? data.timeline : [];
  const respuestas = !activeSnapshot
    ? []
    : timeline.filter((t) => t.tipo === "respuesta_cliente" && t.snapshot_id === activeSnapshot.id);

  const handleSubmitReply = async () => {
    if (sendingReply || !activeSnapshot) return;
    if (!reply.trim() && replyFiles.length === 0) return;
    setSendingReply(true);
    try {
      const adjuntos = [];
      for (const f of replyFiles) {
        const data_url = await readAnyFileAsDataUrl(f);
        adjuntos.push({ nombre: f.name || "archivo", mime: f.type || "", data_url });
      }
      await submitReply({
        mensaje: reply.trim(),
        snapshot_id: activeSnapshotId,
        adjuntos: adjuntos.length ? adjuntos : undefined,
      });
      toast.success("Respuesta enviada");
      await onReload();
      setReply("");
      setReplyFiles([]);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la respuesta");
    } finally {
      setSendingReply(false);
    }
  };

  return (
    <div
      className="min-h-dvh bg-background"
      style={
        data.imagen_fondo
          ? {
              backgroundImage: `url(${data.imagen_fondo})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundAttachment: "fixed",
            }
          : undefined
      }
    >
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-2xl border border-gray-300 bg-white min-h-[50vh] px-4 sm:px-6 py-8 flex flex-col gap-10">
          {(data.imagen_cabecera || data.emoji || EMOJI_PREDETERMINADO) && (
            <div>
              <div className="relative z-0 w-full h-[220px] rounded-lg border overflow-hidden bg-muted/30">
                {data.imagen_cabecera && (
                  <img src={data.imagen_cabecera} alt="" className="w-full h-full object-cover" />
                )}
              </div>
              <div className="relative z-10 -mt-10 mb-0 pl-4 w-fit">
                <span
                  className="text-7xl leading-none select-none inline-block"
                  style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}
                  aria-hidden
                >
                  {data.emoji || EMOJI_PREDETERMINADO}
                </span>
              </div>
            </div>
          )}
          <h1 className="text-4xl font-semibold tracking-tight">{data.revision_titulo || "Revisión"}</h1>
          <Tabs value={activeSnapshotId} onValueChange={setActiveSnapshotId} className="space-y-6">
            <TabsList className="rounded-md border border-input bg-background p-0.5 h-9">
              {snapshots.map((s) => (
                <TabsTrigger key={s.id} value={s.id} className="rounded-[5px]">Revisión {s.numero_revision}</TabsTrigger>
              ))}
            </TabsList>
            {snapshots.map((s) => (
              <TabsContent key={s.id} value={s.id}>
                <h2 className="text-lg font-semibold tracking-tight mb-4">Información</h2>
                <div className="space-y-4">
                  {(s.bloques || []).map((b) => <div key={b.id}>{revisionBlockPreview(b)}</div>)}
                </div>
              </TabsContent>
            ))}
          </Tabs>
          <div className="space-y-3">
            <h2 className="font-medium">Tu respuesta para {activeSnapshot ? `Revisión ${activeSnapshot.numero_revision}` : "esta revisión"}</h2>
            <textarea className="w-full min-h-[120px] rounded-md border bg-background p-3 text-sm" value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Escribe aquí objeciones o cambios..." />
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files || []);
                if (!picked.length) return;
                setReplyFiles((prev) => [...prev, ...picked].slice(0, 10));
                e.target.value = "";
              }}
            />
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" size="sm" className="gap-2" onClick={() => fileInputRef.current?.click()}>
                <Paperclip className="w-4 h-4" />
                Adjuntar archivos
              </Button>
              <span className="text-xs text-muted-foreground">Hasta 10 archivos (~5&nbsp;MB c/u).</span>
            </div>
            {replyFiles.length > 0 && (
              <ul className="flex flex-col gap-1 text-sm">
                {replyFiles.map((f, idx) => (
                  <li key={`${f.name}-${idx}`} className="flex items-center justify-between gap-2 rounded-md border px-2 py-1.5">
                    <span className="truncate min-w-0">{f.name}</span>
                    <button
                      type="button"
                      className="shrink-0 p-1 rounded hover:bg-muted text-muted-foreground"
                      aria-label="Quitar archivo"
                      onClick={() => setReplyFiles((prev) => prev.filter((_, i) => i !== idx))}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <Button
              type="button"
              onClick={handleSubmitReply}
              disabled={(!reply.trim() && replyFiles.length === 0) || sendingReply || !activeSnapshot}
              className="gap-2"
            >
              {sendingReply ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Enviar respuesta
            </Button>
          </div>
          <div className="space-y-3">
            <h2 className="font-medium">Respuestas</h2>
            {respuestas.length === 0 ? (
              <p className="text-sm text-muted-foreground">Sin respuestas todavía.</p>
            ) : (
              respuestas.map((r) => (
                <div key={r.id} className="rounded-md border p-3 space-y-2">
                  {r.fecha_creacion ? (
                    <p className="text-xs text-muted-foreground">{formatFecha(r.fecha_creacion)}</p>
                  ) : null}
                  {r.mensaje?.trim() ? <div className="text-sm whitespace-pre-wrap">{renderLinkedText(r.mensaje)}</div> : null}
                  <RevisionReplyAdjuntos adjuntos={r.adjuntos} />
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
